import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  EyeIcon, 
  ClipboardDocumentIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { ScissorsIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import ClassificationBadge from './ClassificationBadge';
import AuthorityReviewFlag from './AuthorityReviewFlag';
import SCGCitation from './SCGCitation';
import ViewInPdfButton from './ViewInPdfButton';
import { useNavigate } from 'react-router-dom';
import { buildFullClassification } from '../utils/classificationMarking';

const PortionMarkedDocument = ({ paragraphResult, formattedDocumentMutation, adversarialResults = {}, adversarialLoadingKeys = new Set(), useAdversarialTest = false }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('document'); // 'document' or 'reasoning'
  const [expandedParagraphs, setExpandedParagraphs] = useState(new Set());
  const [expandedTextPreviews, setExpandedTextPreviews] = useState(new Set());

  const renderAdversarialPanel = (key, originalConfidence) => {
    const advResult = adversarialResults[key];
    const isLoading = adversarialLoadingKeys.has(key);
    if (!useAdversarialTest || (!advResult && !isLoading)) return null;

    return (
      <div className="mt-3 pt-3 border-t border-[var(--vault-border)]">
        <div className="flex items-center gap-2 mb-2">
          <ShieldExclamationIcon className="w-4 h-4 text-[var(--spotlight-gold)]" />
          <span className="text-xs font-semibold tracking-wider uppercase text-[var(--text-primary)]">Adversarial Self-Test</span>
        </div>
        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <svg className="animate-spin h-3 w-3 text-[var(--spotlight-gold)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Running counter-argument analysis...
          </div>
        )}
        {advResult && !advResult.error && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`stamp text-xs ${
                advResult.recommendation === 'AFFIRM' ? 'stamp-unclassified' :
                advResult.recommendation === 'CHALLENGE' ? 'stamp-secret' :
                'stamp-confidential'
              }`}>{advResult.recommendation}</span>
              <div className="flex items-center gap-3 text-xs">
                {originalConfidence != null && (
                  <span className="text-[var(--text-muted)]">Original: <strong className="text-[var(--text-primary)]">{(originalConfidence * 100).toFixed(0)}%</strong></span>
                )}
                <span className="text-[var(--text-muted)]">Calibrated: <strong className={`${
                  advResult.calibrated_confidence >= (originalConfidence || 0) ? 'text-[var(--success)]' : 'text-[var(--warning)]'
                }`}>{(advResult.calibrated_confidence * 100).toFixed(0)}%</strong></span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="vault-card p-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Over-Class Risk</div>
                <span className={`text-xs font-bold ${advResult.over_classification_risk === 'HIGH' ? 'text-[var(--error)]' : advResult.over_classification_risk === 'MEDIUM' ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>{advResult.over_classification_risk}</span>
              </div>
              <div className="vault-card p-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Under-Class Risk</div>
                <span className={`text-xs font-bold ${advResult.under_classification_risk === 'HIGH' ? 'text-[var(--error)]' : advResult.under_classification_risk === 'MEDIUM' ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>{advResult.under_classification_risk}</span>
              </div>
            </div>
            {advResult.overall_assessment && (
              <div className="vault-card p-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Assessment</div>
                <p className="text-xs text-[var(--text-secondary)]">{advResult.overall_assessment}</p>
              </div>
            )}
          </div>
        )}
        {advResult?.error && (
          <div className="text-xs text-[var(--error)]">Error: {advResult.error}</div>
        )}
      </div>
    );
  };

  // Function to replace SCG rule IDs with SCGCitation components
  const renderScgRulesInText = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Regex to match SCG rule patterns: SCG-XXX, SCG-UNK-XXXXXX-XXX, etc.
    const scgRulePattern = /(SCG-[A-Z0-9-]+)/g;
    
    const parts = text.split(scgRulePattern);
    
    return parts.map((part, index) => {
      if (scgRulePattern.test(part)) {
        return (
          <SCGCitation 
            key={index} 
            ruleId={part} 
            compact={true} 
          />
        );
      }
      return part;
    });
  };


  const handleTearlineClick = () => {
    // Navigate to tearline page with the classified document
    navigate('/tearline', { 
      state: { 
        classifiedDocument: paragraphResult.portion_marked_document,
        documentClassification: paragraphResult.document_classification,
        fullClassification: paragraphResult.full_classification
      } 
    });
  };


  const toggleParagraphExpansion = (paragraphNumber) => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(paragraphNumber)) {
      newExpanded.delete(paragraphNumber);
    } else {
      newExpanded.add(paragraphNumber);
    }
    setExpandedParagraphs(newExpanded);
  };

  const expandAllParagraphs = () => {
    const allParagraphs = new Set(paragraphResult.reasoning?.map(r => r.paragraph_number) || []);
    setExpandedParagraphs(allParagraphs);
  };

  const collapseAllParagraphs = () => {
    setExpandedParagraphs(new Set());
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      {/* Header with Overall Classification */}
      <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Classified Document
              </h3>
              <div className="flex items-center space-x-3 mt-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Overall Classification:</span>
                  <ClassificationBadge 
                    classification={paragraphResult.document_classification}
                    fullClassification={paragraphResult.full_classification}
                    caveats={paragraphResult.caveats}
                    releasability={paragraphResult.releasability}
                    confidence={paragraphResult.document_confidence}
                  />
                  <AuthorityReviewFlag 
                    reasoning={paragraphResult.reasoning} 
                    contradictions={paragraphResult.contradictions}
                    variant="compact" 
                  />
                </div>
                <button
                  onClick={handleTearlineClick}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm"
                  title="Generate Tearline Sanitization"
                >
                  <ScissorsIcon className="w-4 h-4" />
                  <span>Tearline</span>
                </button>
                {paragraphResult?.provenance_decision_id && (
                  <a
                    href={`/provenance/${encodeURIComponent(paragraphResult.provenance_decision_id)}`}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm"
                    title="See every rule retrieved vs cited for this decision"
                  >
                    View provenance
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {paragraphResult.total_paragraphs} paragraph{paragraphResult.total_paragraphs !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveView('document')}
            className={`${
              activeView === 'document'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <DocumentTextIcon className="h-4 w-4" />
            <span>Portion-Marked Document</span>
          </button>
          <button
            onClick={() => setActiveView('reasoning')}
            className={`${
              activeView === 'reasoning'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <InformationCircleIcon className="h-4 w-4" />
            <span>Classification Reasoning</span>
          </button>
        </nav>
      </div>

      {/* Document View */}
      {activeView === 'document' && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              Ready-to-Use Classified Document
            </h4>
            <button
              onClick={() => {
                if (!paragraphResult) {
                  alert('Please wait for classification to complete');
                  return;
                }

                // Build canonical IC marking from result components. Same logic
                // the badge uses, so UI display and downloaded PDF agree.
                const fullClassification = buildFullClassification({
                  classification: paragraphResult.document_classification || 'UNCLASSIFIED',
                  full_classification: paragraphResult.full_classification,
                  caveats: paragraphResult.caveats || [],
                  compartments: paragraphResult.compartments || [],
                  releasability: paragraphResult.releasability || [],
                });

                // Strip heavy LLM-debug fields the formatter never reads. The
                // form-data field that carries this JSON is capped at 1MB by
                // Starlette's multipart parser; retrieved_rules + raw_response
                // routinely push real documents past that limit, causing a 400
                // before the request reaches our handler.
                const stripHeavyFields = ({ retrieved_rules, raw_response, contradictions, ...rest }) => rest;
                const paragraphsForFormatter = (paragraphResult.paragraphs || paragraphResult.reasoning || [])
                  .map(stripHeavyFields);

                const classificationData = {
                  document_classification: fullClassification,
                  paragraph_results: paragraphsForFormatter
                };

                // Try to get file from file input (file upload mode)
                const fileInput = document.querySelector('input[type="file"]');
                let file = fileInput?.files?.[0] || null;

                // If no uploaded file, create a text file from the portion-marked document (text mode)
                if (!file && paragraphResult.portion_marked_document) {
                  const textContent = paragraphResult.portion_marked_document;
                  const blob = new Blob([textContent], { type: 'text/plain' });
                  file = new File([blob], 'classified_document.txt', { type: 'text/plain' });
                }

                if (file) {
                  formattedDocumentMutation?.mutate({
                    file: file,
                    classificationResults: classificationData
                  });
                } else {
                  alert('No document content available for formatting');
                }
              }}
              disabled={formattedDocumentMutation?.isLoading}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              <span>{formattedDocumentMutation?.isLoading ? 'Generating...' : 'Download Formatted'}</span>
            </button>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed">
              {paragraphResult.portion_marked_document}
            </pre>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Usage Instructions:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Each paragraph is marked with its appropriate classification level</li>
                  <li>Copy and paste this document directly into your work</li>
                  <li>Portion marks like (TS//SI//NF) indicate classification and controls</li>
                  <li>Review the "Classification Reasoning" tab for detailed justifications</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reasoning View */}
      {activeView === 'reasoning' && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              Classification Analysis & Reasoning
            </h4>
            <div className="flex space-x-2">
              <button
                onClick={expandAllParagraphs}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Expand All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={collapseAllParagraphs}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Collapse All
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {paragraphResult.reasoning?.map((reasoning, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleParagraphExpansion(reasoning.paragraph_number)}
                  className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-gray-900">
                      Paragraph {reasoning.paragraph_number}
                    </span>
                    <span className="px-2 py-1 bg-white text-gray-900 border border-gray-400 text-xs rounded font-mono">
                      {reasoning.classification}
                    </span>
                    <span className="text-sm text-gray-600">
                      {Math.round(reasoning.confidence * 100)}% confidence
                    </span>
                    <AuthorityReviewFlag 
                      reasoning={reasoning.reasoning}
                      contradictions={reasoning.contradictions}
                      variant="compact" 
                    />
                  </div>
                  {expandedParagraphs.has(reasoning.paragraph_number) ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {expandedParagraphs.has(reasoning.paragraph_number) && (
                  <div className="px-4 py-3 border-t border-gray-200">
                    <div className="space-y-3">
                      {/* Text Content */}
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Text Content
                          </label>
                          {reasoning.text_preview && reasoning.text_preview.length > 200 && (
                            <button
                              onClick={() => {
                                const newSet = new Set(expandedTextPreviews);
                                if (newSet.has(reasoning.paragraph_number)) {
                                  newSet.delete(reasoning.paragraph_number);
                                } else {
                                  newSet.add(reasoning.paragraph_number);
                                }
                                setExpandedTextPreviews(newSet);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {expandedTextPreviews.has(reasoning.paragraph_number) ? 'Show less' : 'Show full text'}
                            </button>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-2 rounded whitespace-pre-wrap">
                          {reasoning.text_preview && reasoning.text_preview.length > 200 && !expandedTextPreviews.has(reasoning.paragraph_number)
                            ? reasoning.text_preview.slice(0, 200) + '...'
                            : reasoning.text_preview}
                        </div>
                      </div>

                      {/* SCG Citations */}
                      {reasoning.scg_citations && reasoning.scg_citations.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            SCG Rule Citations
                          </label>
                          <ul className="mt-1 space-y-1">
                            {reasoning.scg_citations.map((citation, citationIndex) => {
                              if (citation && typeof citation === 'object') {
                                const supporting = citation.friendly_reference || citation.title || '';
                                return (
                                  <li key={citationIndex} className="text-sm text-blue-700 bg-blue-50 p-2 rounded flex items-start gap-2">
                                    {citation.rule_id && (
                                      <SCGCitation ruleId={citation.rule_id} compact={true} />
                                    )}
                                    {citation.rule_id && (
                                      <ViewInPdfButton ruleId={citation.rule_id} variant="icon" size="sm" />
                                    )}
                                    {supporting && (
                                      <span className="text-gray-700">{supporting}</span>
                                    )}
                                  </li>
                                );
                              }
                              return (
                                <li key={citationIndex} className="text-sm text-blue-700 bg-blue-50 p-2 rounded">
                                  {renderScgRulesInText(citation)}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Detailed Reasoning */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Classification Reasoning
                        </label>
                        <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                          <div className="whitespace-pre-wrap font-sans">
                            {renderScgRulesInText(reasoning.reasoning || 'No detailed reasoning available')}
                          </div>
                        </div>
                      </div>

                      {/* Adversarial Self-Test (per paragraph) */}
                      {renderAdversarialPanel(index, reasoning.confidence)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {(!paragraphResult.reasoning || paragraphResult.reasoning.length === 0) && (
            <div className="text-center py-8">
              <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No reasoning available</h3>
              <p className="mt-1 text-sm text-gray-500">
                Classification reasoning was not provided for this document.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortionMarkedDocument;